import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Shield,
  Briefcase,
  User,
  Key,
  CheckCircle,
  XCircle,
  Eye,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";

import { Utensils, Tent } from "lucide-react";

const ROLES = {
  admin: { label: "Admin", icon: Shield, color: "text-red-500" },
  agent: { label: "Agente", icon: Briefcase, color: "text-blue-500" },
  client: { label: "Cliente B2B", icon: User, color: "text-green-500" },
  sendme_admin: { label: "SendMe Admin", icon: Utensils, color: "text-yellow-500" },
  sendme_conference_admin: { label: "SendMe Conference", icon: Tent, color: "text-orange-500" },
};

export default function AgentsPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "agent",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // We fetch ALL users now, not just agents
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento utenti");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
        });
        toast.success("Utente aggiornato");
      } else {
        await api.post("/auth/register", formData);
        toast.success("Utente creato");
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;
    
    try {
      await api.put(`/users/${selectedUser.id}/password`, { password: newPassword });
      toast.success("Password aggiornata con successo");
      setPasswordDialogOpen(false);
      setNewPassword("");
      setSelectedUser(null);
    } catch (error) {
      toast.error("Errore nell'aggiornamento della password");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utente eliminato");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleToggleActive = async (user) => {
    const newStatus = !user.is_active;
    const action = newStatus ? "attivare" : "disattivare";
    
    if (!window.confirm(`Sei sicuro di voler ${action} questo utente?`)) return;
    
    try {
      await api.put(`/users/${user.id}`, { is_active: newStatus });
      toast.success(`Utente ${newStatus ? "attivato" : "disattivato"} con successo`);
      fetchUsers();
    } catch (error) {
      toast.error(`Errore durante ${action} dell'utente`);
    }
  };

  const openDetailDialog = (user) => {
    setDetailUser(user);
    setDetailDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      password: "", // Password not needed for edit (handled separately)
    });
    setDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setPasswordDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "agent",
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestione Utenti</h1>
          <p className="text-muted-foreground">Admin, Agenti e Clienti B2B</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingUser ? "Modifica Utente" : "Nuovo Utente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome Completo</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="agent">Agente</SelectItem>
                    <SelectItem value="client">Cliente B2B</SelectItem>
                    <SelectItem value="sendme_admin">SendMe Admin</SelectItem>
                    <SelectItem value="sendme_conference_admin">SendMe Conference</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    required
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90">
                  {editingUser ? "Aggiorna" : "Crea"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => {
              const RoleIcon = ROLES[user.role]?.icon || User;
              return (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <RoleIcon className={`w-4 h-4 ${ROLES[user.role]?.color}`} />
                      <span className="capitalize">{ROLES[user.role]?.label || user.role}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.is_active === false ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                        In attesa
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                        Attivo
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {user.role === "agent" && (
                          <DropdownMenuItem onClick={() => openDetailDialog(user)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Vedi Anagrafica
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openEditDialog(user)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Modifica Dati
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                          {user.is_active === false ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                              Attiva Utente
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-2 text-amber-600" />
                              Disattiva Utente
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openPasswordDialog(user)}>
                          <Key className="w-4 h-4 mr-2" />
                          Cambia Password
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(user.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambia Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label>Nuova Password per {selectedUser?.name}</Label>
              <Input
                required
                type="password"
                placeholder="Minimo 8 caratteri"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-accent hover:bg-accent/90">
                Salva Nuova Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Agent Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anagrafica Agente</DialogTitle>
          </DialogHeader>
          {detailUser && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-2">
                {detailUser.is_active === false ? (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                    In attesa di approvazione
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                    Attivo
                  </Badge>
                )}
                {detailUser.source === "web_registration" && (
                  <Badge variant="secondary">Registrazione Web</Badge>
                )}
              </div>

              {/* Personal Data */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="col-span-2">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-3">Dati Personali</h3>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Nome Completo</Label>
                  <p className="font-medium">{detailUser.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Codice Fiscale</Label>
                  <p className="font-mono">{detailUser.fiscal_code || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Data di Nascita</Label>
                  <p>{detailUser.birth_date || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Luogo di Nascita</Label>
                  <p>{detailUser.birth_place || "-"}</p>
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50/50 rounded-lg">
                <div className="col-span-2">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-3">Contatti</h3>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="font-medium">{detailUser.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Telefono</Label>
                  <p>{detailUser.phone || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Cellulare</Label>
                  <p>{detailUser.mobile || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">PEC</Label>
                  <p className="font-mono text-sm">{detailUser.pec || "-"}</p>
                </div>
              </div>

              {/* Fiscal Data */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-green-50/50 rounded-lg">
                <div className="col-span-2">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-3">Dati Fiscali</h3>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Partita IVA</Label>
                  <p className="font-mono">{detailUser.vat_number || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Codice SDI</Label>
                  <p className="font-mono">{detailUser.sdi_code || "-"}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">Iscrizione ENASARCO</Label>
                  <p className="font-mono">{detailUser.enasarco_number || "-"}</p>
                </div>
              </div>

              {/* Address */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="col-span-2">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-3">Indirizzo</h3>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-muted-foreground">Indirizzo</Label>
                  <p>{detailUser.address || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Città</Label>
                  <p>{detailUser.city || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">CAP / Provincia</Label>
                  <p>{detailUser.postal_code || "-"} {detailUser.region ? `(${detailUser.region})` : ""}</p>
                </div>
              </div>

              {/* Extra Info */}
              {(detailUser.areas || detailUser.experience || detailUser.notes) && (
                <div className="grid grid-cols-1 gap-4 p-4 bg-amber-50/50 rounded-lg">
                  <h3 className="font-semibold text-sm text-muted-foreground">Informazioni Aggiuntive</h3>
                  {detailUser.areas && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Zone di Competenza</Label>
                      <p>{detailUser.areas}</p>
                    </div>
                  )}
                  {detailUser.experience && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Esperienza</Label>
                      <p className="text-sm">{detailUser.experience}</p>
                    </div>
                  )}
                  {detailUser.notes && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Note</Label>
                      <p className="text-sm">{detailUser.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Chiudi
                </Button>
                {detailUser.is_active === false && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      handleToggleActive(detailUser);
                      setDetailDialogOpen(false);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Attiva Agente
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
